import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Instagram, Youtube, Linkedin, ArrowUp } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useSiteContactSettings } from '../hooks/useSiteContactSettings';

const QUICK_LINKS = [
  { to: '/about', labelKey: 'footer.links.about' },
  { to: '/services', labelKey: 'footer.links.services' },
  { to: '/talents', labelKey: 'footer.links.talents' },
  { to: '/work', labelKey: 'footer.links.work' },
  { to: '/spotlight', labelKey: 'footer.links.spotlight' },
];

const iconBadgeCls =
  'flex items-center justify-center w-9 h-9 rounded-full bg-white/5 text-gray-300 transition-all duration-300 hover:bg-[#b3d130] hover:text-[#313131] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b3d130] focus-visible:ring-offset-2 focus-visible:ring-offset-[#313131]';

const Footer = () => {
  const { t } = useLanguage();
  const settings = useSiteContactSettings();
  const SOCIAL_LINKS: { icon: typeof Facebook; label: string; href: string | null }[] = [
    { icon: Facebook, label: 'Facebook', href: settings.facebook_url },
    { icon: Instagram, label: 'Instagram', href: settings.instagram_url },
    { icon: Youtube, label: 'YouTube', href: settings.youtube_url },
    { icon: Linkedin, label: 'LinkedIn', href: settings.linkedin_url },
  ];
  return (
    <footer className="bg-[#313131] text-white pt-12 pb-8 border-t border-[#b3d130]/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Logo and Description */}
          <div className="space-y-4">
            <Link to="/" className="inline-block">
              <img
                src="/assets/arteria-logo.png"
                alt="ARTERIA"
                className="h-12 brightness-0 invert hover:scale-105 transition-transform duration-300"
              />
            </Link>
            <p className="text-gray-300 text-sm leading-relaxed max-w-xs">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-[#b3d130]">
              {t('footer.quickLinks.title')}
            </h3>
            <nav className="flex flex-col space-y-2.5">
              {QUICK_LINKS.map(({ to, labelKey }) => (
                <Link
                  key={to}
                  to={to}
                  className="group inline-flex items-center text-gray-300 hover:text-[#b3d130] transition-colors duration-300 text-sm"
                >
                  <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
                    {t(labelKey)}
                  </span>
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-[#b3d130]">{t('footer.contact.title')}</h3>
            <div className="space-y-3">
              <a
                href={`mailto:${settings.contact_email}`}
                className="group flex items-center gap-3 text-gray-300 hover:text-[#b3d130] transition-colors duration-300 text-sm"
              >
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 text-[#b3d130] shrink-0 transition-colors duration-300 group-hover:bg-[#b3d130] group-hover:text-[#313131]">
                  <Mail size={14} />
                </span>
                {settings.contact_email}
              </a>
              <a
                href={`tel:${settings.contact_phone}`}
                className="group flex items-center gap-3 text-gray-300 hover:text-[#b3d130] transition-colors duration-300 text-sm"
              >
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 text-[#b3d130] shrink-0 transition-colors duration-300 group-hover:bg-[#b3d130] group-hover:text-[#313131]">
                  <Phone size={14} />
                </span>
                {settings.contact_phone}
              </a>
              <a
                href={settings.map_url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-start gap-3 text-gray-300 hover:text-[#b3d130] transition-colors duration-300 text-sm"
              >
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 text-[#b3d130] shrink-0 transition-colors duration-300 group-hover:bg-[#b3d130] group-hover:text-[#313131]">
                  <MapPin size={14} />
                </span>
                <span className="pt-1.5">{settings.address}</span>
              </a>
            </div>
          </div>

          {/* Social Media */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-[#b3d130]">{t('footer.social.title')}</h3>
            <div className="flex gap-3">
              {SOCIAL_LINKS.map(({ icon: Icon, label, href }) =>
                href ? (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className={iconBadgeCls}
                  >
                    <Icon size={16} />
                  </a>
                ) : (
                  <span
                    key={label}
                    aria-disabled="true"
                    title={t('footer.social.comingSoon', { label })}
                    className={`${iconBadgeCls} opacity-40 cursor-not-allowed hover:bg-white/5 hover:text-gray-300`}
                  >
                    <Icon size={16} />
                  </span>
                ),
              )}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-4">
          <p className="text-gray-400 text-sm text-center sm:text-left">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-[#b3d130] transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b3d130] rounded"
          >
            {t('footer.backToTop')} <ArrowUp size={14} />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
